import React from 'react';
import { useDidUpdateEffect } from '@odin-labs/components';
import { getJobsiteMoment } from 'utils/dates';
import { Player, PlayerProps } from 'components/player';
import { useJobsitePulse } from 'containers/jobsite/tabs/jobsitePulse/components/JobsitePulseProvider';
import {
  DroneMainCamera,
  isIdleDroneCamera,
  useDroneDeploy,
} from 'containers/jobsite/tabs/jobsitePulse/components/drones';
import { useManifestLoadingProgress } from './useManifestLoadingProgress';
import { FileMainCamera } from './FileMainCamera';
import {
  VideoFrame,
  VideoCaption,
  VideoUnavailable,
  VideoLoadingProgress,
} from './video';
import { styles } from './styles';

export function MainCamera(): React.ReactElement {
  const { hlsBasicAuth, date, time, shortTimeZone } = useJobsitePulse();
  const { selectedCamera } = useDroneDeploy()!;
  const [hasError, setHasError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const [currentPlayTime, setCurrentPlayTime] = React.useState('');

  const {
    loadingProgress,
    onXhrSetup,
    reset: resetLoadingProgress,
  } = useManifestLoadingProgress();

  useDidUpdateEffect(() => {
    setHasError(false);
    setCurrentPlayTime('');
    resetLoadingProgress();
  }, [selectedCamera?.url]);

  const onErrorHandler: PlayerProps['onError'] = React.useCallback(
    (_error, data): void => {
      if (data?.fatal) {
        setHasError(true);
      }
    },
    [],
  );

  const onProgressHandler: PlayerProps['onProgress'] = React.useCallback(
    (value) => {
      const { playedSeconds } = value;
      if (playedSeconds) {
        const jobsiteMoment = getJobsiteMoment({ date, time });
        const currentMoment = jobsiteMoment.add(playedSeconds, 'seconds');
        setCurrentPlayTime(
          `${currentMoment.format('hh:mm:ss a')} ${shortTimeZone}`,
        );
      }
    },
    [date, time, shortTimeZone],
  );

  const retry = React.useCallback((): void => {
    setHasError(false);
    setRetryCount((count) => count + 1);
    resetLoadingProgress();
  }, [resetLoadingProgress]);

  const hlsOptions = React.useMemo((): PlayerProps['hlsOptions'] => {
    return hlsBasicAuth
      ? {
          xhrSetup: (xhr: XMLHttpRequest, url: string): void => {
            xhr.setRequestHeader('Authorization', `Basic ${hlsBasicAuth}`);
            Object.assign(xhr, { withCredentials: true });
            onXhrSetup(xhr, url);
          },
          // debug: true,
        }
      : undefined;
  }, [hlsBasicAuth, onXhrSetup]);

  const isVideoUnavailable =
    hasError ||
    // if hasFootage is false, it means we know for certain that
    // there is no footage available for this camera
    // (as opposed to null which means we don't know either way)
    (selectedCamera && selectedCamera.hasFootage === false);

  // Drone deploy demo — renders map/status/feed phases for an idle drone camera
  if (isIdleDroneCamera(selectedCamera)) {
    return (
      <DroneMainCamera
        selectedCamera={selectedCamera}
        className={styles.mainCamera}
      />
    );
  }

  // Uploaded-video fallback for jobsites with no Avigilon cameras
  if (selectedCamera?.type === 'file') {
    return <FileMainCamera selectedCamera={selectedCamera} />;
  }

  return (
    <div className={styles.mainCamera}>
      <VideoFrame>
        <VideoLoadingProgress progress={loadingProgress} />
        <VideoCaption text={selectedCamera?.name} suppressWhenNoText />
        {selectedCamera?.isLive ? (
          <VideoCaption text="LIVE" color="purple" align="right" />
        ) : (
          <VideoCaption
            text={currentPlayTime}
            align="right"
            suppressWhenNoText
          />
        )}
        {isVideoUnavailable ? (
          <VideoUnavailable retry={hasError ? retry : undefined} />
        ) : (
          hlsOptions && (
            <Player
              key={retryCount.toString()} // Force remount on retry
              url={selectedCamera?.url}
              hlsOptions={hlsOptions}
              className="odin-h-full odin-w-full"
              onError={onErrorHandler}
              onProgress={
                selectedCamera && !selectedCamera.isLive
                  ? onProgressHandler
                  : undefined
              }
              enableFastForward
              customControls
              playing
              muted
            />
          )
        )}
      </VideoFrame>
    </div>
  );
}
